import './RankAthleteDetail.css';

import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  parsePerCoachComments,
  parseSkillFieldValue,
} from '../../../services/athleteService';
import { AthleteWithId } from '../../../types/athlete';
import {
  FormField,
  FormGroup,
  FormSchema,
  GENERAL_COMMENTS_FIELD_ID,
  GENERAL_COMMENTS_GROUP_ID,
  getSkillConfig,
} from '../../../types/formSchema';
import { BarsArrowDownIcon, BarsArrowUpIcon } from '../../../pages/TryoutIcons';
import AthleteCardTop from '../AthleteCardTop/AthleteCardTop';
import BackArrowIcon from '../BackButton/BackArrowIcon';
import BackButton from '../BackButton/BackButton';
import FullscreenPhotoViewer
  from '../FullscreenPhotoViewer/FullscreenPhotoViewer';
import PerCoachCommentsSection
  from '../PerCoachCommentsSection/PerCoachCommentsSection';
import ScrollToTopFab from '../ScrollToTopFab/ScrollToTopFab';
import SectionLauncherButton
  from '../SectionLauncherButton/SectionLauncherButton';
import SkillJudge from '../SkillJudge/SkillJudge';
import VideoViewer from '../VideoViewer/VideoViewer';

const SCROLL_TOP_FAB_THRESHOLD_PX = 180;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RankAthleteDetailProps {
  athlete: AthleteWithId;
  formSchema: FormSchema;
  teams: string[];
  onClose: () => void;
  onTeamPlacementChange: (teamPlacement: string | null) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  /** Override video rendering — used by the demo to substitute DemoVideoViewer. */
  renderVideo?: (videoUid: string, label: string) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const RankAthleteDetail: React.FC<RankAthleteDetailProps> = ({
  athlete,
  formSchema,
  teams,
  onClose,
  onTeamPlacementChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  renderVideo,
}) => {
  const [activeGroupId, setActiveGroupId] = useState('');
  const [cardCompact, setCardCompact] = useState(false);
  const [viewerPhoto, setViewerPhoto] = useState<{ src: string; alt: string } | null>(null);
  const [scrollTopFabVisible, setScrollTopFabVisible] = useState(false);
  const [expandedView, setExpandedView] = useState(() => {
    try { return localStorage.getItem('rankDetail.expandedView') === 'true'; } catch { return false; }
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLElement>(null);

  const MOBILE_MQ = '(max-width: 768px)';
  const COMPACT_ENTER_PX = 32;
  const COMPACT_EXIT_PX = 2;

  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;
    const mq = window.matchMedia(MOBILE_MQ);
    const handleScroll = (): void => {
      const top = scrollEl.scrollTop;
      if (!mq.matches) { setCardCompact(false); setScrollTopFabVisible(false); return; }
      if (!activeGroupId && !expandedView) { setCardCompact(false); setScrollTopFabVisible(false); return; }
      setCardCompact((prev) => {
        if (!prev && top > COMPACT_ENTER_PX) return true;
        if (prev && top <= COMPACT_EXIT_PX) return false;
        return prev;
      });
      setScrollTopFabVisible(expandedView && top > SCROLL_TOP_FAB_THRESHOLD_PX);
    };
    const handleMqChange = (): void => {
      if (!mq.matches) setCardCompact(false);
      else handleScroll();
    };
    handleScroll();
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    mq.addEventListener('change', handleMqChange);
    return () => {
      scrollEl.removeEventListener('scroll', handleScroll);
      mq.removeEventListener('change', handleMqChange);
    };
  }, [athlete.id, activeGroupId, expandedView]);

  useEffect(() => {
    const scrollEl = scrollRef.current;
    const cardEl = cardRef.current;
    if (!scrollEl || !cardEl) return;
    const update = (): void => {
      scrollEl.style.setProperty('--rank-detail-card-height', `${cardEl.offsetHeight}px`);
    };
    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(cardEl);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [athlete.id, activeGroupId]);

  const groups = useMemo(
    () => [...formSchema.groups].sort((a, b) => a.order - b.order),
    [formSchema.groups],
  );

  const activeGroup = groups.find((g) => g.id === activeGroupId) ?? null;

  const groupsWithContent = useMemo(
    () =>
      groups.filter((group) => {
        if (group.id === GENERAL_COMMENTS_GROUP_ID) {
          return parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID]).length > 0;
        }
        return group.fields.some((f) => {
          if (f.type === 'heading') return false;
          const val = athlete.scores[f.id];
          if (!val) return false;
          const parsed = parseSkillFieldValue(val);
          return (
            (typeof parsed.rating === 'number' && parsed.rating > 0)
            || (parsed.comments?.length ?? 0) > 0
            || parsed.photo !== undefined
            || parsed.video !== undefined
          );
        });
      }),
    [groups, athlete.scores],
  );

  const imageSrc = athlete.imageThumbnailUrl || athlete.imageUrl || null;
  const imageFullSrc = athlete.imageUrl || athlete.imageThumbnailUrl || null;

  const handleScrollToTop = (): void => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const handleToggleView = (): void => {
    if (!expandedView) setActiveGroupId('');
    setExpandedView((prev) => {
      const next = !prev;
      try { localStorage.setItem('rankDetail.expandedView', String(next)); } catch { /* storage unavailable */ }
      return next;
    });
  };

  const handleTeamPress = (team: string): void => {
    onTeamPlacementChange(athlete.teamPlacement === team ? null : team);
  };

  const renderReadOnlyField = (field: FormField): React.ReactNode => {
    if (field.type === 'heading') {
      return <h3 className="rank-detail-heading-field">{field.label}</h3>;
    }

    const cfg = getSkillConfig(field);
    const raw = athlete.scores[field.id];
    const fieldValue = parseSkillFieldValue(raw);
    const ratingValue = fieldValue.rating ?? 0;
    const commentsValue = fieldValue.comments ?? [];
    const photoValue = fieldValue.photo ?? null;
    const videoValue = fieldValue.video ?? null;

    const hasRating = cfg.rating && ratingValue > 0;
    const hasComments = cfg.comment && commentsValue.length > 0;
    const hasPhoto = cfg.photo && photoValue !== null;
    const hasVideo = cfg.video && videoValue !== null;

    if (!hasRating && !hasComments && !hasPhoto && !hasVideo) return null;

    return (
      <div className="rank-detail-skill-row">
        {cfg.rating ? (
          <SkillJudge title={field.label} value={ratingValue} maxRating={cfg.maxRating} />
        ) : (
          <h3 className="rank-detail-skill-title">{field.label}</h3>
        )}
        {hasComments ? (
          <PerCoachCommentsSection comments={commentsValue} />
        ) : null}
        {hasPhoto && photoValue ? (
          <div className="rank-detail-photo-wrap">
            <button
              type="button"
              className="rank-detail-photo-btn"
              onClick={() => setViewerPhoto({
                src: photoValue.imageUrl,
                alt: field.label,
              })}
              aria-label={`View ${field.label} photo`}
            >
              <img
                className="rank-detail-photo-img"
                src={photoValue.imageThumbnailUrl || photoValue.imageUrl}
                alt={field.label}
              />
            </button>
          </div>
        ) : null}
        {hasVideo && videoValue ? (
          <div className="rank-detail-video-wrap">
            {renderVideo
              ? renderVideo(videoValue.videoUid, field.label)
              : <VideoViewer videoUid={videoValue.videoUid} label={field.label} />}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="rank-athlete-detail">
      {/* Nav */}
      <header className="rank-athlete-detail-nav">
        <BackButton onClick={onClose} ariaLabel="Back to ranking list" />
        <div className="rank-athlete-detail-nav-actions">
          <button
            type="button"
            className="rank-athlete-detail-nav-btn"
            onClick={onPrevious}
            disabled={!hasPrevious}
            aria-label="Previous athlete"
          >
            ‹ Prev
          </button>
          <button
            type="button"
            className="rank-athlete-detail-nav-btn rank-athlete-detail-nav-btn--next"
            onClick={onNext}
            disabled={!hasNext}
            aria-label="Next athlete"
          >
            Next ›
          </button>
        </div>
      </header>

      {/* Scrollable content */}
      <div ref={scrollRef} className="rank-athlete-detail-scroll">
        {/* Sticky athlete info card */}
        <section
          ref={cardRef}
          className={`rank-athlete-detail-card${cardCompact ? ' rank-athlete-detail-card--compact' : ''}`}
        >
          <AthleteCardTop
            name={athlete.name}
            number={athlete.number}
            position={athlete.position}
            returningstatus={athlete.returningstatus}
            imageSrc={imageSrc}
            compact={cardCompact}
            onPhotoClick={imageFullSrc ? () => setViewerPhoto({ src: imageFullSrc, alt: athlete.name }) : undefined}
            rightSlot={
              <button
                type="button"
                className={`rank-detail-view-toggle${expandedView ? ' rank-detail-view-toggle--active' : ''}`}
                onClick={handleToggleView}
                aria-label={expandedView ? 'Switch to section view' : 'Switch to expanded view'}
                aria-pressed={expandedView}
              >
                {expandedView ? (
                  <BarsArrowUpIcon size={18} strokeWidth={2} />
                ) : (
                  <BarsArrowDownIcon size={18} strokeWidth={2} />
                )}
              </button>
            }
          />
        </section>

        {/* Section content */}
        <main
          className={`rank-athlete-detail-content${teams.length > 0 ? ' rank-athlete-detail-content--with-footer' : ''}`}
        >
          {expandedView ? (
            <section className="rank-detail-expanded-view">
              {groupsWithContent.length === 0 ? (
                <p className="rank-detail-no-sections">No scores recorded yet.</p>
              ) : (
                groupsWithContent.map((group) => (
                  <div key={group.id} className="rank-detail-expanded-group">
                    <div className="rank-detail-expanded-group-header">
                      <h3 className="rank-detail-section-title">{group.label}</h3>
                    </div>
                    {group.id === GENERAL_COMMENTS_GROUP_ID ? (
                      <div className="rank-detail-fields-list">
                        <PerCoachCommentsSection
                          comments={parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID])}
                        />
                      </div>
                    ) : (
                      <div className="rank-detail-fields-list">
                        {[...group.fields]
                          .sort((a, b) => a.order - b.order)
                          .map((field) => {
                            const rendered = renderReadOnlyField(field);
                            if (rendered === null) return null;
                            return <div key={field.id}>{rendered}</div>;
                          })}
                      </div>
                    )}
                  </div>
                ))
              )}
            </section>
          ) : activeGroup ? (
            <section className="rank-detail-fields">
              <button
                type="button"
                className="rank-detail-section-header"
                onClick={() => setActiveGroupId('')}
                aria-label="Back to sections"
              >
                <span className="rank-detail-back-icon" aria-hidden="true">
                  <BackArrowIcon />
                </span>
                <h3 className="rank-detail-section-title">{activeGroup.label}</h3>
              </button>
              {activeGroup.id === GENERAL_COMMENTS_GROUP_ID ? (
                <PerCoachCommentsSection
                  comments={parsePerCoachComments(athlete.scores[GENERAL_COMMENTS_FIELD_ID])}
                />
              ) : (
                <div className="rank-detail-fields-list">
                  {[...activeGroup.fields]
                    .sort((a, b) => a.order - b.order)
                    .map((field) => {
                      const rendered = renderReadOnlyField(field);
                      if (rendered === null) return null;
                      return <div key={field.id}>{rendered}</div>;
                    })}
                </div>
              )}
            </section>
          ) : (
            <section className="rank-detail-section-launcher">
              {groups.length === 0 ? (
                <p className="rank-detail-no-sections">No scoring sections for this tryout.</p>
              ) : (
                groups.map((group: FormGroup) => {
                  if (group.id === GENERAL_COMMENTS_GROUP_ID) {
                    const commentCount = parsePerCoachComments(
                      athlete.scores[GENERAL_COMMENTS_FIELD_ID],
                    ).length;
                    return (
                      <SectionLauncherButton
                        key={group.id}
                        label={group.label}
                        scoredCount={commentCount}
                        scorableCount={commentCount}
                        isGeneralComments
                        onClick={() => setActiveGroupId(group.id)}
                        ariaLabel="View General Comments"
                      />
                    );
                  }
                  const scorableFields = group.fields.filter((f) => f.type !== 'heading');
                  const scoredFields = scorableFields.filter((f) => {
                    const val = athlete.scores[f.id];
                    if (!val) return false;
                    const parsed = parseSkillFieldValue(val);
                    return (
                      (typeof parsed.rating === 'number' && parsed.rating > 0)
                      || (parsed.comments?.length ?? 0) > 0
                      || parsed.photo !== undefined
                      || parsed.video !== undefined
                    );
                  });
                  return (
                    <SectionLauncherButton
                      key={group.id}
                      label={group.label}
                      scoredCount={scoredFields.length}
                      scorableCount={scorableFields.length}
                      onClick={() => setActiveGroupId(group.id)}
                      ariaLabel={`View ${group.label} scores`}
                    />
                  );
                })
              )}
            </section>
          )}
        </main>
      </div>

      {viewerPhoto ? (
        <FullscreenPhotoViewer
          open={true}
          src={viewerPhoto.src}
          alt={viewerPhoto.alt}
          viewerLabel={`${viewerPhoto.alt} photo`}
          onClose={() => setViewerPhoto(null)}
        />
      ) : null}

      <ScrollToTopFab visible={scrollTopFabVisible} onPress={handleScrollToTop} />

      {/* Footer: team placement + prev/next nav */}
      <footer className="rank-athlete-detail-footer">
        {teams.length > 0 ? (
          <div className="rank-athlete-detail-footer-teams">
            {teams.map((team) => (
              <button
                key={team}
                type="button"
                className={[
                  'rank-athlete-detail-team-btn',
                  athlete.teamPlacement === team ? 'rank-athlete-detail-team-btn--active' : '',
                ].filter(Boolean).join(' ')}
                onClick={() => handleTeamPress(team)}
              >
                {team}
              </button>
            ))}
          </div>
        ) : null}
      </footer>
    </div>
  );
};

export default RankAthleteDetail;
